import { Component, ChangeDetectionStrategy } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, map } from 'rxjs';

import { DetectionFilterKeys } from '../../../../enums/detection-filter';
import { FilterSlide } from '../../../../models/filter-slide';
import { TranslocoService } from '@jsverse/transloco';
import { EmergencyTemplateLabel } from '../../../../models/emergency-template';

import {
  selectAllDetections,
  selectDetectionEmergencyTemplate,
  selectDetectionFilters,
} from '../../../../features/detection/detection.selectors';

import * as DetectionActions from '../../../../features/detection/detection.actions';

@Component({
    selector: 'detection-filters',
    templateUrl: './detection-filters.component.html',
    styleUrls: ['./detection-filters.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class DetectionFiltersComponent {
  detectionFilterKeysEnum = DetectionFilterKeys;

  readonly detections$ = this.store.select(selectAllDetections);
  readonly filters$ = this.store.select(selectDetectionFilters);
  readonly emergencyTemplate$ = this.store.select(selectDetectionEmergencyTemplate);

  // Status affichés (labels emergency + confirmed/to_confirm)
  readonly status$ = this.emergencyTemplate$.pipe(
    map((emergencyTemplate) => {
      const status: Array<{ value: string; label: string }> = [];

      if (emergencyTemplate) {
        emergencyTemplate.labels.forEach((item: EmergencyTemplateLabel) => {
          status.push({ value: String(item.level), label: item.label });
        });
        status.push({ value: '0', label: this.translocoService.translate('to_confirm') });
      } else {
        status.push({ value: '0', label: this.translocoService.translate('to_confirm') });
        status.push({ value: '-1', label: this.translocoService.translate('confirmed') });
      }

      return status;
    })
  );

  // Slider: max basé sur detections, valeurs basées sur filters
  readonly confidenceFilterSlide$ = combineLatest([this.detections$, this.filters$]).pipe(
    map(([detections, filters]) => {
      const slide = new FilterSlide();
      const max = this.max(detections, 'confidence') * 100;

      slide.max = max || 0;

      const conf = filters?.[DetectionFilterKeys.CONFIDENCES];
      if (!conf) {
        slide.start = 0;
        slide.end = slide.max;
      } else {
        slide.start = conf.start;
        slide.end = conf.end;
      }

      return slide;
    })
  );

  constructor(
    private store: Store,
    private translocoService: TranslocoService
  ) {}

  // ✅ checkbox selected = lecture depuis filters
  isSelected(filters: any, value: string, key: string): boolean {
    const arr = filters?.[key];
    return Array.isArray(arr) ? arr.includes(value) : false;
  }

  // ✅ toggle checkbox = dispatch patch
  toggleValue(filters: any, value: string, key: string): void {
    const current: string[] = Array.isArray(filters?.[key]) ? [...filters[key]] : [];
    const idx = current.indexOf(value);

    if (idx >= 0) current.splice(idx, 1);
    else current.push(value);

    this.store.dispatch(
      DetectionActions.updateDetectionFilters({
        patch: { [key]: current } as any
      })
    );
  }

  // ✅ slider change = dispatch patch
  updateConfidence(slide?: FilterSlide): void {
    if (!slide) return;

    this.store.dispatch(
      DetectionActions.updateDetectionFilters({
        patch: {
          [DetectionFilterKeys.CONFIDENCES]: { start: slide.start, end: slide.end }
        } as any
      })
    );
  }


  private max(detections: Array<any>, key: string): number {
    if (!detections?.length) return 0;
    return detections
      .map(d => (d ? d[key] : 0))
      .reduce((prev, cur) => (prev > cur ? prev : cur), 0);
  }
}
